import { ChangeEvent, FormEvent, useEffect, useState } from "react";
import { storeUser } from "helpers";
import { Button, ButtonVariant, CardHeader, InputWithButton, InputWithButtonI, ValidationMessage } from "components";
import { Modal } from "features";
import { confirmSignIn, fetchAuthSession } from "aws-amplify/auth";
import { useLazyGetProfileQuery, useRegisterDeviceMutation } from "services";
import { useAuthContext } from "context";
import { UserCognito, PreferredMFA, ChallengeName } from "helpers";
import { t } from "i18next";
import { useTranslation } from "react-i18next";

const initialData: InputWithButtonI = {
  value: "",
  status: "initial",
  error: "",
};

export const Auth2FA = () => {
  const { t } = useTranslation();
  const { setAuth, setUser } = useAuthContext();

  const [code, setCode] = useState<InputWithButtonI>(initialData);
  const [disabledCode, setDisabledCode] = useState<boolean>(true);
  const [disabled, setDisabled] = useState<boolean>(true);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const [getProfile] = useLazyGetProfileQuery();
  const [registerDevice, { isLoading, isError, error }] = useRegisterDeviceMutation();

  const returnCodeText = code.status === "initial" ? t("common.confirm") : code.status === "success" ? t("common.confirmed") : t("common.tryAgain");
  const returnButtonVariant = ({ status }: InputWithButtonI): ButtonVariant =>
    status === "initial" ? "bg-primary" : status === "success" ? "bg-success" : "bg-error";

  const onChangeHandler = (event: ChangeEvent<HTMLInputElement>): void => {
    const {
      target: { value },
    } = event;
    setCode((oldObject) => ({
      ...oldObject,
      value,
    }));
  };

  const storeAdditionalUserData = async (username: string, deviceName: string, deviceKey: any) => {
    localStorage.setItem("username", username);
    localStorage.setItem("deviceName", deviceName);
    if (deviceKey) {
      localStorage.setItem("deviceKey", deviceKey);
      sendDeviceData(username, deviceKey, deviceName);
    } else {
      console.warn("No device_key found in access token payload");
    }
  };

  const onSubmitHandler = (event: FormEvent<HTMLFormElement | HTMLButtonElement>): void => {
    event.preventDefault();
    if (code.status !== "success") onVerifyCode(code.value);
  };
  const onVerifyCode = async (code: string) => {
    try {
      setDisabledCode(true);
      const result = await confirmSignIn({
        challengeResponse: code,
      });
      console.log("confirmSignIn result:", result);

      // After successful 2FA, fetch session and create user object
      const session = await fetchAuthSession();
      if (!session.tokens) {
        throw new Error("No authentication tokens available after 2FA");
      }

      const payload = session.tokens.idToken?.payload;
      const accessToken = session.tokens.accessToken;
      const idToken = session.tokens.idToken;

      if (!payload || !accessToken || !idToken) {
        throw new Error("Missing required token data (payload, accessToken, or idToken)");
      }

      const cognitoUsername = payload["cognito:username"];
      const username = typeof cognitoUsername === "string" ? cognitoUsername : localStorage.getItem("username") || "";

      // Explicitly type preferredMFA and challengeName
      const preferredMFA: PreferredMFA = payload["cognito:preferred_mfa_setting"] === "SMS" ? "SMS_MFA" : "NOMFA";
      const challengeName: ChallengeName = "SMS_MFA";
      const deviceName = navigator.userAgent;
      const accessTokenPayload = session.tokens?.accessToken.payload;
      const deviceKey = accessTokenPayload?.device_key || "";
      await storeAdditionalUserData(username, deviceName, deviceKey);
      // console.log(deviceName, deviceKey, accessTokenPayload);

      const user: UserCognito = {
        username,
        accessToken: accessToken.toString(),
        idToken: idToken.toString(),
        attributes: {
          sub: payload.sub as string,
          email: payload.email as string,
          phone_number: (payload.phone_number as string) || "",
          email_verified: payload.email_verified === true,
          phone_number_verified: payload.phone_number_verified === true,
        },
        preferredMFA,
        challengeName,
      };
      console.log(user);

      storeUser(user);
      setUser(user);
      setCode((oldObject) => ({
        ...oldObject,
        status: "success",
      }));

      getProfile();
    } catch (err: any) {
      console.error(err);
      setCode((oldObject) => ({
        ...oldObject,
        error: err.message || t("auth.invalidCode"),
        status: "error",
      }));
    } finally {
      setDisabledCode(false);
    }
  };

  const sendDeviceData = async (username: string, deviceKey: string, deviceName: string) => {
    try {
      const result = await registerDevice({ username, deviceKey, deviceName }).unwrap();
      console.log("Device registration result:", result);
    } catch (error) {
      console.error("Error registering device:", error);
    }
  };

  const onContinueHandler = () => {
    setAuth("success");
  };

  useEffect(() => {
    if (code.error) {
      setCode((oldObject) => ({
        ...oldObject,
        error: "",
      }));
    }
    setDisabledCode(code.value.length !== 6);
  }, [code.value]);

  useEffect(() => {
    setDisabled(code.status !== "success");
  }, [code.status]);

  return (
    <div className="space-y-5">
      <CardHeader
        title={t("auth.twoFactorAuth")}
        paragraph={t("auth.twoFactorDesc")}
      />
      <form onSubmit={onSubmitHandler}>
        <div className="flex flex-col">
          <div className="flex">
            <InputWithButton
              inputProps={{
                id: "code",
                name: "code",
                value: code.value,
                onChange: onChangeHandler,
                placeholder: t("auth.enterVerificationCode"),
                maxLength: 6,
                minLength: 6,
                readOnly: code.status === "success",
              }}
              buttonProps={{
                type: "submit",
                variant: returnButtonVariant(code),
                disabled: disabledCode || isLoading,
                className: "min-w-[150px] max-w-[150px] px-2",
                children: returnCodeText,
              }}
            />
          </div>
          <ValidationMessage>{code.error || (isError ? t("messages.loadError") : "")}</ValidationMessage>
          <div className="mt-5 flex flex-1">
            {code.status !== "success" && (
              <div className="flex flex-1 items-center justify-center">
                <button type="button" className="text-sm text-primary underline" onClick={() => setIsModalOpen(true)}>
                  {t("auth.resendCode")}
                </button>
              </div>
            )}
            <div className="flex flex-1 justify-center">
              <Button
                disabled={disabled || isLoading}
                className={code.status === "success" ? "mx-auto mt-5 px-16 sm:min-w-[200px]" : undefined}
                onClick={onContinueHandler}
              >
                {t("auth.login")}
              </Button>
            </div>
          </div>
        </div>
      </form>

      <Modal open={isModalOpen} setOpen={setIsModalOpen} onlyCloseButton closeButton={t("common.close")} withActions={false}>
        <h2 className="text-center text-xl font-semibold">{t("auth.cantFindSms")}</h2>
        <p className="mt-4">
          {t("auth.smsHelpText")}
        </p>
      </Modal>
    </div>
  );
};
